/**
 * ============================================================================
 * METRIC DEFINITIONS API — RTK Query endpoint for getMetricDefinitions
 * ============================================================================
 *
 * The metric catalog is constant for a dashboard/review, so it is fetched
 * ONCE per session: cache key = {dashboardId, reviewType}, keepUnusedDataFor: Infinity,
 * no refetch on focus. Fires in PARALLEL with getMetrics — presentation
 * (names, goals, groups) and values arrive independently and are joined by
 * metricKey in the selectors.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { GetMetricDefinitionsApi } from 'src/accessors/GetMetricDefinitionsApi';
import { ReportingApiError } from 'src/accessors/ReportingApiClient';
import {
  GetMetricDefinitionsInput,
  GetMetricDefinitionsOutput,
} from 'src/types/metricsApi.types';

export const metricDefinitionsApi = createApi({
  reducerPath: 'metricDefinitionsApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(),
  keepUnusedDataFor: Infinity,
  refetchOnFocus: false,
  endpoints: (builder) => ({
    getMetricDefinitions: builder.query<GetMetricDefinitionsOutput, GetMetricDefinitionsInput>({
      queryFn: async (input) => {
        try {
          return { data: await GetMetricDefinitionsApi.getMetricDefinitions(input) };
        } catch (error) {
          if (error instanceof ReportingApiError) {
            return { error: { status: error.status, data: error.body ?? error.errorCode } };
          }
          return { error: { status: 0, data: String(error) } };
        }
      },
    }),
  }),
});

export function useGetMetricDefinitionsQuery(
  input: GetMetricDefinitionsInput,
  options?: { skip?: boolean },
) {
  return metricDefinitionsApi.useGetMetricDefinitionsQuery(input, { skip: options?.skip ?? false });
}
